import { MapPin, Clock, CheckCircle, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const mainLocation = {
  name: "Meinerzhagen",
  description: "Hauptstandort für Einzel- und Gruppentraining",
  isMain: true
};

const additionalLocations = [
  {
    name: "Drolshagen",
    description: "SC Drolshagen"
  },
  {
    name: "Plettenberg",
    description: "TuS Plettenberg"
  },
  {
    name: "Oestertal",
    description: "Region Plettenberg"
  }
];

const features = [
  "Flexible Trainingszeiten nach Absprache",
  "Einzel- und Gruppentraining möglich",
  "Weitere Standorte auf Anfrage",
  "Training auch an Wochenenden"
];

export const LocationsSection = () => {
  return (
    <section
      id="standorte"
      className="py-20 lg:py-32 bg-[#0A0A0A]"
      data-testid="locations-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-[#c1ff72] font-semibold mb-4">
            Flexibel & Regional
          </p>
          <h2 className="font-['Bebas_Neue'] text-4xl md:text-5xl lg:text-6xl uppercase tracking-tighter text-white mb-6">
            TRAININGSSTANDORTE &{" "}
            <span className="text-[#c1ff72]">FLEXIBLE TERMINE</span>
          </h2>
          <p className="text-[#A1A1AA] text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
            Training aktuell in Meinerzhagen, Plettenberg, Oestertal und Drolshagen – 
            weitere Standorte nach Absprache möglich.
          </p>
        </div>

        {/* Locations Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          
          {/* Main Location Card */}
          <div className="bg-gradient-to-br from-[#c1ff72]/10 to-transparent border border-[#c1ff72]/30 p-8 md:p-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#c1ff72] flex items-center justify-center">
                <MapPin className="w-6 h-6 text-[#0A0A0A]" />
              </div>
              <div>
                <span className="text-[#c1ff72] text-xs font-semibold uppercase tracking-wider">
                  Hauptstandort
                </span>
                <h3 className="font-['Bebas_Neue'] text-3xl md:text-4xl text-white">
                  {mainLocation.name}
                </h3>
              </div>
            </div>
            <p className="text-[#A1A1AA] text-lg mb-6">
              {mainLocation.description}
            </p>
            
            {/* Features List */}
            <ul className="space-y-3">
              {features.map((feature) => (
                <li key={feature} className="flex items-center gap-3 text-white">
                  <CheckCircle className="w-5 h-5 text-[#c1ff72] flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Additional Locations */}
          <div className="space-y-4">
            <h4 className="font-['Bebas_Neue'] text-2xl text-white mb-6 flex items-center gap-3">
              <span className="w-8 h-[2px] bg-[#c1ff72]"></span>
              WEITERE STANDORTE
            </h4>
            
            {additionalLocations.map((location) => (
              <div
                key={location.name}
                className="bg-[#1A1A1A] border border-white/5 p-6 hover:border-[#c1ff72]/30 transition-all duration-300 group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-[#0A0A0A] border border-white/10 flex items-center justify-center group-hover:border-[#c1ff72]/30 transition-colors">
                    <MapPin className="w-5 h-5 text-[#c1ff72]" />
                  </div>
                  <div>
                    <h5 className="font-['Bebas_Neue'] text-xl text-white">
                      {location.name}
                    </h5>
                    <p className="text-[#71717A] text-sm">
                      {location.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}

            {/* Custom Location Box */}
            <div className="bg-[#121212] border border-dashed border-[#c1ff72]/30 p-6 mt-6">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-[#c1ff72]/10 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-[#c1ff72]" />
                </div>
                <div>
                  <h5 className="font-medium text-white">
                    Dein Wunschstandort?
                  </h5>
                  <p className="text-[#71717A] text-sm">
                    Frag einfach an – ich bin flexibel!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Flexible Times Info */}
        <div className="bg-[#121212] border border-white/5 p-8 md:p-10">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#c1ff72]/20 flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-[#c1ff72]" />
              </div>
              <div>
                <h4 className="font-['Bebas_Neue'] text-2xl text-white mb-2">
                  FLEXIBLE TERMINABSPRACHE
                </h4>
                <p className="text-[#A1A1AA] max-w-xl">
                  Trainingszeiten passe ich individuell an deine Verfügbarkeit an. 
                  Ob vormittags, nachmittags oder am Wochenende – wir finden den passenden Termin.
                </p>
              </div>
            </div>
            <Link to="/buchen">
              <Button
                className="bg-[#c1ff72] hover:bg-[#d4ff9a] text-[#0A0A0A] font-semibold px-8 py-6 rounded-none transition-all duration-300 whitespace-nowrap"
              >
                <Calendar className="w-5 h-5 mr-2" />
                JETZT BUCHEN
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LocationsSection;
